import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import UserContext from "./UserContext";
import ValetApi from "../api/Api";
import { getDateInputValueInTimeZone } from "../utility/dateInput";
import "./ValidationPortal.css";

function normalizeLocations(userDetail) {
    const explicitLocations = Array.isArray(userDetail?.accessibleLocations) ? userDetail.accessibleLocations : [];
    if (explicitLocations.length) {
        return explicitLocations
            .map((location) => ({
                id: Number(location.id),
                name: location.name || `Location ${location.id}`,
            }))
            .filter((location) => Number.isInteger(location.id) && location.id > 0);
    }

    const podiums = Array.isArray(userDetail?.accessiblePodiums) ? userDetail.accessiblePodiums : [];
    const ids = Array.from(new Set(podiums.map((podium) => Number(podium.locationId)).filter((id) => Number.isInteger(id) && id > 0)));
    return ids.map((id) => ({ id, name: `Location ${id}` }));
}

function formatCents(cents, currency = "usd") {
    const value = Number(cents || 0) / 100;
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: String(currency || "usd").toUpperCase(),
        }).format(value);
    } catch {
        return `$${value.toFixed(2)}`;
    }
}

function formatPercent(value) {
    const normalized = Number(value || 0);
    if (!Number.isFinite(normalized)) return "0.0%";
    return `${normalized.toFixed(1)}%`;
}

function defaultMonthValue() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

function shiftDateIso(isoDate, deltaDays) {
    const date = new Date(`${isoDate}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + Number(deltaDays || 0));
    return date.toISOString().slice(0, 10);
}

function getMonthStartIso(monthText, todayIso) {
    const source = /^\d{4}-\d{2}$/.test(String(monthText || "")) ? String(monthText) : todayIso.slice(0, 7);
    return `${source}-01`;
}

function getMonthEndIso(monthText) {
    if (!/^\d{4}-\d{2}$/.test(String(monthText || ""))) return null;
    const date = new Date(`${monthText}-01T00:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() + 1);
    date.setUTCDate(0);
    return date.toISOString().slice(0, 10);
}

function computeRangeFromPreset(preset, { todayIso, selectedMonth }) {
    if (preset === "today") {
        return { startDate: todayIso, endDate: todayIso };
    }

    if (preset === "week") {
        return { startDate: shiftDateIso(todayIso, -6), endDate: todayIso };
    }

    if (preset === "month") {
        const monthStart = getMonthStartIso(selectedMonth, todayIso);
        const monthEnd = getMonthEndIso(selectedMonth);
        const resolvedEndDate = monthEnd && monthEnd < todayIso ? monthEnd : todayIso;
        return { startDate: monthStart, endDate: resolvedEndDate };
    }

    if (preset === "last60") {
        return { startDate: shiftDateIso(todayIso, -59), endDate: todayIso };
    }

    if (preset === "last90") {
        return { startDate: shiftDateIso(todayIso, -89), endDate: todayIso };
    }

    return null;
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

const ValidationReportsPage = () => {
    const { userDetail } = useContext(UserContext);
    const roleId = Number(userDetail?.roleId || 0);
    const isTenantPortal = Boolean(userDetail?.isTenantPortal);

    const userTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const todayIso = getDateInputValueInTimeZone(new Date(), userTimeZone);
    const monthStart = `${todayIso.slice(0, 8)}01`;

    const accessibleLocations = useMemo(() => normalizeLocations(userDetail), [userDetail]);
    const accessiblePodiums = useMemo(() => {
        const podiums = Array.isArray(userDetail?.accessiblePodiums) ? userDetail.accessiblePodiums : [];
        return podiums
            .map((podium) => ({
                id: Number(podium.id),
                name: podium.name || `Podium ${podium.id}`,
                locationId: Number(podium.locationId),
            }))
            .filter((podium) => Number.isInteger(podium.id) && podium.id > 0);
    }, [userDetail]);

    const [selectedLocationId, setSelectedLocationId] = useState("");
    const [reportRange, setReportRange] = useState("today");
    const [startDate, setStartDate] = useState(monthStart);
    const [endDate, setEndDate] = useState(todayIso);
    const [report, setReport] = useState(null);
    const [isLoadingReport, setIsLoadingReport] = useState(false);

    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState("");
    const [invoiceMonth, setInvoiceMonth] = useState(defaultMonthValue());
    const [invoiceEmailTo, setInvoiceEmailTo] = useState("");
    const [invoiceEmailMessage, setInvoiceEmailMessage] = useState("");
    const [invoice, setInvoice] = useState(null);
    const [invoiceMode, setInvoiceMode] = useState("month");
    const [itemizedTransactions, setItemizedTransactions] = useState([]);
    const [isLoadingItemized, setIsLoadingItemized] = useState(false);
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
    const [isSendingInvoiceEmail, setIsSendingInvoiceEmail] = useState(false);
    const [bulkScopeType, setBulkScopeType] = useState("location");
    const [bulkScopeId, setBulkScopeId] = useState("");
    const [bulkInvoices, setBulkInvoices] = useState([]);
    const [bulkScopeMeta, setBulkScopeMeta] = useState(null);
    const [isLoadingBulkInvoices, setIsLoadingBulkInvoices] = useState(false);
    const printInProgressRef = useRef(false);
    const printCleanupTimerRef = useRef(null);

    useEffect(() => {
        if (!accessibleLocations.length) {
            setSelectedLocationId("");
            return;
        }

        const preferredId = Number(userDetail?.locationId || accessibleLocations[0].id);
        const hasPreferred = accessibleLocations.some((location) => location.id === preferredId);
        setSelectedLocationId(String(hasPreferred ? preferredId : accessibleLocations[0].id));
    }, [accessibleLocations, userDetail]);

    useEffect(() => {
        if (bulkScopeType !== "location") return;
        if (selectedLocationId) {
            setBulkScopeId(String(selectedLocationId));
            return;
        }

        if (accessibleLocations.length) {
            setBulkScopeId(String(accessibleLocations[0].id));
        }
    }, [bulkScopeType, selectedLocationId, accessibleLocations]);

    useEffect(() => {
        if (bulkScopeType !== "podium") return;

        setBulkScopeId((prev) => {
            if (prev && accessiblePodiums.some((podium) => Number(podium.id) === Number(prev))) {
                return prev;
            }
            return accessiblePodiums.length ? String(accessiblePodiums[0].id) : "";
        });
    }, [bulkScopeType, accessiblePodiums]);

    useEffect(() => {
        return () => {
            if (typeof document !== "undefined") {
                document.body.classList.remove("validation-print-single", "validation-print-bulk");
                document.documentElement.classList.remove("validation-print-single", "validation-print-bulk");
            }
            if (printCleanupTimerRef.current) {
                window.clearTimeout(printCleanupTimerRef.current);
                printCleanupTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (reportRange === "custom") return;

        const computed = computeRangeFromPreset(reportRange, {
            todayIso,
            selectedMonth: invoiceMonth,
        });

        if (!computed) return;
        setStartDate(computed.startDate);
        setEndDate(computed.endDate);
    }, [reportRange, todayIso, invoiceMonth]);

    useEffect(() => {
        async function loadReport() {
            if (!selectedLocationId && !isTenantPortal) {
                setReport(null);
                return;
            }

            try {
                setIsLoadingReport(true);
                if (isTenantPortal) {
                    const res = await ValetApi.getTenantValidationUsage({
                        range: "custom",
                        startDate: startDate || null,
                        endDate: endDate || null,
                    });
                    const usage = res?.data?.usage || null;
                    const summary = usage?.summary || null;
                    const tenant = usage?.tenant || null;
                    setReport({
                        tenant,
                        range: usage?.range,
                        startDate: usage?.startDate,
                        endDate: usage?.endDate,
                        tenants:
                            tenant && summary
                                ? [
                                      {
                                          tenantId: tenant.id,
                                          tenantName: tenant.name,
                                          printedCount: Number(summary.printedCount || 0),
                                          redeemedCount: Number(summary.redeemedCount || 0),
                                          redeemedAmountCents: Number(summary.redeemedAmountCents || 0),
                                      },
                                  ]
                                : [],
                    });
                } else {
                    const res = await ValetApi.getValidationLocationReport(Number(selectedLocationId), {
                        startDate: startDate || null,
                        endDate: endDate || null,
                    });
                    setReport(res?.data?.report || null);
                }
            } catch (err) {
                setReport(null);
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load validation report", { autoClose: 1600 });
            } finally {
                setIsLoadingReport(false);
            }
        }

        loadReport();
    }, [selectedLocationId, startDate, endDate, isTenantPortal, reportRange]);

    useEffect(() => {
        async function loadTenants() {
            if (isTenantPortal) {
                try {
                    const res = await ValetApi.getTenantValidationProfile();
                    const tenant = res?.data?.profile || null;
                    if (!tenant?.id) {
                        setTenants([]);
                        setSelectedTenantId("");
                        return;
                    }

                    setTenants([tenant]);
                    setSelectedTenantId(String(tenant.id));
                    setSelectedLocationId(String(tenant.locationId || ""));
                } catch {
                    setTenants([]);
                    setSelectedTenantId("");
                }
                return;
            }

            if (!selectedLocationId) {
                setTenants([]);
                setSelectedTenantId("");
                return;
            }

            try {
                const res = await ValetApi.listValidationTenantsByLocation(Number(selectedLocationId), true);
                let rows = Array.isArray(res?.data?.tenants) ? res.data.tenants : [];

                if (!rows.length && accessibleLocations.length > 1) {
                    const fallbackResponses = await Promise.all(accessibleLocations.filter((location) => Number(location.id) !== Number(selectedLocationId)).map((location) => ValetApi.listValidationTenantsByLocation(Number(location.id), true).catch(() => null)));

                    const fallbackRows = fallbackResponses.flatMap((response) => (Array.isArray(response?.data?.tenants) ? response.data.tenants : []));
                    rows = fallbackRows;
                }

                setTenants(rows);
                setSelectedTenantId((prev) => {
                    if (prev && rows.some((row) => Number(row.id) === Number(prev))) return prev;
                    return rows.length ? String(rows[0].id) : "";
                });
            } catch (err) {
                setTenants([]);
                setSelectedTenantId("");
            }
        }

        loadTenants();
    }, [selectedLocationId, isTenantPortal]);

    async function handleLoadInvoice() {
        if (!selectedTenantId) {
            toast.error("Select a tenant", { autoClose: 1200 });
            return;
        }

        if (invoiceMode === "month" && !invoiceMonth) {
            toast.error("Select invoice month", { autoClose: 1200 });
            return;
        }

        if (invoiceMode === "range" && (!startDate || !endDate)) {
            toast.error("Select start and end date", { autoClose: 1200 });
            return;
        }

        try {
            setIsLoadingInvoice(true);
            const res = invoiceMode === "month" ? (isTenantPortal ? await ValetApi.getTenantValidationMonthlyInvoice(invoiceMonth) : await ValetApi.getValidationTenantMonthlyInvoice(Number(selectedTenantId), invoiceMonth)) : isTenantPortal ? await ValetApi.getTenantValidationRangeInvoice({ startDate, endDate }) : await ValetApi.getValidationTenantRangeInvoice(Number(selectedTenantId), { startDate, endDate });
            const loadedInvoice = res?.data?.invoice || null;
            setInvoice(loadedInvoice);
            setItemizedTransactions([]);
            if (loadedInvoice?.tenant?.contactEmail) {
                setInvoiceEmailTo(String(loadedInvoice.tenant.contactEmail));
            }
        } catch (err) {
            setInvoice(null);
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load tenant invoice", { autoClose: 1600 });
        } finally {
            setIsLoadingInvoice(false);
        }
    }

    async function handleViewItemizedTransactions() {
        if (!selectedTenantId) {
            toast.error("Select a tenant", { autoClose: 1200 });
            return;
        }

        if (!startDate || !endDate) {
            toast.error("Select start and end date", { autoClose: 1200 });
            return;
        }

        try {
            setIsLoadingItemized(true);
            const res = isTenantPortal ? await ValetApi.getTenantValidationRangeInvoice({ startDate, endDate }) : await ValetApi.getValidationTenantRangeInvoice(Number(selectedTenantId), { startDate, endDate });

            const loadedInvoice = res?.data?.invoice || null;
            setInvoice(loadedInvoice);
            setInvoiceMode("range");
            setItemizedTransactions(Array.isArray(loadedInvoice?.lineItems) ? loadedInvoice.lineItems : []);
            if (loadedInvoice?.tenant?.contactEmail) {
                setInvoiceEmailTo(String(loadedInvoice.tenant.contactEmail));
            }
        } catch (err) {
            setItemizedTransactions([]);
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load itemized transactions", { autoClose: 1600 });
        } finally {
            setIsLoadingItemized(false);
        }
    }

    async function handleEmailInvoice() {
        if (isTenantPortal) {
            toast.info("Invoice email sending is handled by your parking operations team.", { autoClose: 1800 });
            return;
        }

        if (!selectedTenantId || !invoiceMonth) {
            toast.error("Select tenant and month", { autoClose: 1200 });
            return;
        }

        try {
            setIsSendingInvoiceEmail(true);
            const res = await ValetApi.emailValidationTenantMonthlyInvoice(Number(selectedTenantId), invoiceMonth, {
                toEmail: String(invoiceEmailTo || "").trim() || undefined,
                message: String(invoiceEmailMessage || "").trim() || undefined,
            });

            const sent = Boolean(res?.data?.sent);
            const reason = res?.data?.delivery?.reason || null;

            if (sent) {
                toast.success(`Invoice emailed to ${res?.data?.toEmail || "recipient"}`, { autoClose: 1800 });
            } else {
                toast.info(`Email not sent (${reason || "delivery skipped"}).`, { autoClose: 2500 });
            }
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to email tenant invoice", { autoClose: 1800 });
        } finally {
            setIsSendingInvoiceEmail(false);
        }
    }

    async function handleLoadBulkInvoices() {
        if (!bulkScopeId) {
            toast.error(`Select a ${bulkScopeType}`, { autoClose: 1200 });
            return;
        }

        if (invoiceMode === "month" && !invoiceMonth) {
            toast.error("Select invoice month", { autoClose: 1200 });
            return;
        }

        if (invoiceMode === "range" && (!startDate || !endDate)) {
            toast.error("Select start and end date", { autoClose: 1200 });
            return;
        }

        try {
            setIsLoadingBulkInvoices(true);
            const res =
                invoiceMode === "month"
                    ? await ValetApi.getValidationBulkMonthlyInvoices({
                          month: invoiceMonth,
                          scopeType: bulkScopeType,
                          scopeId: Number(bulkScopeId),
                      })
                    : await ValetApi.getValidationBulkRangeInvoices({
                          startDate,
                          endDate,
                          scopeType: bulkScopeType,
                          scopeId: Number(bulkScopeId),
                      });

            const invoices = Array.isArray(res?.data?.invoices) ? res.data.invoices : [];
            setBulkInvoices(invoices);
            setBulkScopeMeta(res?.data?.scope || null);
            toast.success(`Loaded ${invoices.length} tenant invoices`, { autoClose: 1200 });
        } catch (err) {
            setBulkInvoices([]);
            setBulkScopeMeta(null);
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load bulk invoices", { autoClose: 1600 });
        } finally {
            setIsLoadingBulkInvoices(false);
        }
    }

    async function fetchSingleInvoiceForPrint() {
        if (!selectedTenantId && !isTenantPortal) {
            toast.error("Select a tenant", { autoClose: 1200 });
            return null;
        }

        if (invoiceMode === "month") {
            if (!invoiceMonth) {
                toast.error("Select invoice month", { autoClose: 1200 });
                return null;
            }

            const res = isTenantPortal ? await ValetApi.getTenantValidationMonthlyInvoice(invoiceMonth) : await ValetApi.getValidationTenantMonthlyInvoice(Number(selectedTenantId), invoiceMonth);
            return res?.data?.invoice || null;
        }

        if (!startDate || !endDate) {
            toast.error("Select start and end date", { autoClose: 1200 });
            return null;
        }

        const res = isTenantPortal ? await ValetApi.getTenantValidationRangeInvoice({ startDate, endDate }) : await ValetApi.getValidationTenantRangeInvoice(Number(selectedTenantId), { startDate, endDate });
        return res?.data?.invoice || null;
    }

    async function handlePrintSingleDocument() {
        const selectedInvoice = invoice || null;
        let printInvoice = selectedInvoice;

        // Ensure print uses freshly loaded line items, matching bulk print reliability.
        if (!printInvoice?.lineItems?.length) {
            try {
                printInvoice = await fetchSingleInvoiceForPrint();
                if (printInvoice) {
                    setInvoice(printInvoice);
                    setItemizedTransactions(Array.isArray(printInvoice?.lineItems) ? printInvoice.lineItems : []);
                }
            } catch (err) {
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load invoice for printing", { autoClose: 1600 });
                return;
            }
        }

        if (!printInvoice) {
            toast.error("Load invoice before printing", { autoClose: 1200 });
            return;
        }

        const printWindow = window.open("", "_blank", "width=1100,height=900");
        if (!printWindow) {
            toast.error("Popup blocked. Please allow popups and try again.", { autoClose: 1800 });
            return;
        }

        const printLines = Array.isArray(printInvoice?.lineItems) ? printInvoice.lineItems : [];
        const billingLabel = invoiceMode === "month" ? `Billing Month: ${printInvoice?.month || invoiceMonth || "--"}` : `Billing Range: ${printInvoice?.startDate || startDate || "--"} to ${printInvoice?.endDate || endDate || "--"}`;
        const itemizedHeaderLabel = `${printInvoice?.tenant?.name || "Tenant"} • ${billingLabel}`;

        const lineRows = printLines
            .map((row) => {
                const redeemedAt = row?.redeemedAt ? new Date(row.redeemedAt).toLocaleString() : "--";
                return `<tr>
                    <td>${escapeHtml(redeemedAt)}</td>
                    <td>${escapeHtml(row?.tokenCode || "")}</td>
                    <td>${escapeHtml(row?.ticketNum || "--")}</td>
                    <td>${escapeHtml(row?.coverageMode || "")}</td>
                    <td>${escapeHtml(formatCents(row?.redeemedAmountCents || 0))}</td>
                </tr>`;
            })
            .join("");

        const printableHtml = `<!doctype html>
            <html>
                <head>
                    <meta charset="utf-8" />
                    <title>Tenant Validation Invoice</title>
                    <style>
                        @page { margin: 0.55in; }
                        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #102338; margin: 0; }
                        h1 { margin: 0 0 8px; font-size: 22px; }
                        h2 { margin: 0 0 10px; font-size: 18px; }
                        .billing-label { margin: 0 0 12px; color: #2d4f74; font-weight: 600; }
                        .summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
                        .summary-item { border: 1px solid #d2dbe8; border-radius: 8px; padding: 8px; }
                        .summary-item span { display: block; font-size: 11px; text-transform: uppercase; color: #5b6f86; margin-bottom: 4px; }
                        .summary-item strong { font-size: 14px; color: #0f2238; }
                        .invoice-itemized { padding-top: 0.7in; }
                        table { width: 100%; border-collapse: collapse; font-size: 12px; }
                        th, td { border: 1px solid #d5deea; padding: 6px 7px; text-align: left; }
                        th { background: #f1f5fb; }
                        .itemized-tenant-header { font-size: 11px; color: #2d4f74; }
                    </style>
                </head>
                <body>
                    <section class="invoice-cover ${printLines.length ? "page-break-after" : ""}">
                        <h1>Tenant Validation Invoice</h1>
                        <p class="billing-label">${escapeHtml(billingLabel)}</p>
                        <div class="summary-grid">
                            <div class="summary-item"><span>Tenant</span><strong>${escapeHtml(printInvoice?.tenant?.name || "--")}</strong></div>
                            <div class="summary-item"><span>Location</span><strong>${escapeHtml(printInvoice?.tenant?.locationId ? `Location ${printInvoice.tenant.locationId}` : "--")}</strong></div>
                            <div class="summary-item"><span>Total Line Items</span><strong>${escapeHtml(printInvoice?.totals?.lineItemsCount || printLines.length || 0)}</strong></div>
                            <div class="summary-item"><span>Total Due</span><strong>${escapeHtml(formatCents(printInvoice?.totals?.totalAmountCents || 0))}</strong></div>
                        </div>
                    </section>
                    ${
                        printLines.length
                            ? `<section class="invoice-itemized">
                        <h2>Itemized Transactions (${escapeHtml(printLines.length)})</h2>
                        <table>
                            <thead>
                                <tr>
                                    <th colspan="5" class="itemized-tenant-header">${escapeHtml(itemizedHeaderLabel)}</th>
                                </tr>
                                <tr>
                                    <th>Redeemed At</th>
                                    <th>Token</th>
                                    <th>Ticket</th>
                                    <th>Mode</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>${lineRows}</tbody>
                        </table>
                    </section>`
                            : ""
                    }
                </body>
            </html>`;

        try {
            printWindow.document.open();
            printWindow.document.write(printableHtml);
            printWindow.document.close();
        } catch {
            printWindow.document.body.innerHTML = '<p style="font-family:sans-serif;padding:16px;">Unable to render printable document. Please close this window and try again.</p>';
            toast.error("Unable to render printable document", { autoClose: 1600 });
            return;
        }

        const triggerPrint = () => {
            try {
                printWindow.focus();
                printWindow.print();
            } catch {
                toast.error("Unable to open print dialog", { autoClose: 1600 });
            }
        };

        printWindow.onload = () => {
            window.setTimeout(triggerPrint, 60);
        };
        window.setTimeout(triggerPrint, 220);
    }

    function handlePrintBulkDocument() {
        if (!bulkInvoices.length) {
            toast.error("Load invoices before printing", { autoClose: 1200 });
            return;
        }

        const printWindow = window.open("", "_blank", "width=1100,height=900");
        if (!printWindow) {
            toast.error("Popup blocked. Please allow popups and try again.", { autoClose: 1800 });
            return;
        }

        const sectionsHtml = bulkInvoices
            .map((bulkInvoice, idx) => {
                const bulkLines = Array.isArray(bulkInvoice?.lineItems) ? bulkInvoice.lineItems : [];
                const isLastTenant = idx === bulkInvoices.length - 1;
                const billingLabel = invoiceMode === "month" ? `Billing Month: ${bulkInvoice?.month || invoiceMonth}` : `Billing Range: ${bulkInvoice?.range?.startDate || startDate || "--"} to ${bulkInvoice?.range?.endDate || endDate || "--"}`;
                const itemizedHeaderLabel = `${bulkInvoice?.tenant?.name || "Tenant"} • ${billingLabel}`;
                const coverBreakClass = bulkLines.length || !isLastTenant ? "page-break-after" : "";
                const itemizedBreakClass = !isLastTenant ? "page-break-after" : "";

                const lineRows = bulkLines
                    .map((row) => {
                        const redeemedAt = row?.redeemedAt ? new Date(row.redeemedAt).toLocaleString() : "--";
                        return `<tr>
                            <td>${escapeHtml(redeemedAt)}</td>
                            <td>${escapeHtml(row?.tokenCode || "")}</td>
                            <td>${escapeHtml(row?.ticketNum || "--")}</td>
                            <td>${escapeHtml(row?.coverageMode || "")}</td>
                            <td>${escapeHtml(formatCents(row?.redeemedAmountCents || 0))}</td>
                        </tr>`;
                    })
                    .join("");

                return `
                    <section class="invoice-cover ${coverBreakClass}">
                        <h1>Tenant Validation Invoice</h1>
                        <p class="billing-label">${escapeHtml(billingLabel)}</p>
                        <div class="summary-grid">
                            <div class="summary-item"><span>Tenant</span><strong>${escapeHtml(bulkInvoice?.tenant?.name || "--")}</strong></div>
                            <div class="summary-item"><span>Location</span><strong>${escapeHtml(bulkInvoice?.tenant?.locationId ? `Location ${bulkInvoice.tenant.locationId}` : "--")}</strong></div>
                            <div class="summary-item"><span>Total Line Items</span><strong>${escapeHtml(bulkInvoice?.totals?.lineItemsCount || bulkLines.length || 0)}</strong></div>
                            <div class="summary-item"><span>Total Due</span><strong>${escapeHtml(formatCents(bulkInvoice?.totals?.totalAmountCents || 0))}</strong></div>
                        </div>
                    </section>
                    ${
                        bulkLines.length
                            ? `<section class="invoice-itemized ${itemizedBreakClass}">
                        <h2>Itemized Transactions (${escapeHtml(bulkLines.length)})</h2>
                        <table>
                            <thead>
                                <tr>
                                    <th colspan="5" class="itemized-tenant-header">${escapeHtml(itemizedHeaderLabel)}</th>
                                </tr>
                                <tr>
                                    <th>Redeemed At</th>
                                    <th>Token</th>
                                    <th>Ticket</th>
                                    <th>Mode</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>${lineRows}</tbody>
                        </table>
                    </section>`
                            : ""
                    }
                `;
            })
            .join("");

        const printableHtml = `<!doctype html>
            <html>
                <head>
                    <meta charset="utf-8" />
                    <title>Bulk Tenant Validation Invoices</title>
                    <style>
                        @page { margin: 0.55in; }
                        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #102338; margin: 0; }
                        h1 { margin: 0 0 8px; font-size: 22px; }
                        h2 { margin: 0 0 10px; font-size: 18px; }
                        .billing-label { margin: 0 0 12px; color: #2d4f74; font-weight: 600; }
                        .summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
                        .summary-item { border: 1px solid #d2dbe8; border-radius: 8px; padding: 8px; }
                        .summary-item span { display: block; font-size: 11px; text-transform: uppercase; color: #5b6f86; margin-bottom: 4px; }
                        .summary-item strong { font-size: 14px; color: #0f2238; }
                        .invoice-itemized { padding-top: 0.7in; }
                        table { width: 100%; border-collapse: collapse; font-size: 12px; }
                        th, td { border: 1px solid #d5deea; padding: 6px 7px; text-align: left; }
                        th { background: #f1f5fb; }
                        .itemized-tenant-header { font-size: 11px; color: #2d4f74; }
                        .page-break-after { page-break-after: always; break-after: page; }
                    </style>
                </head>
                <body>
                    ${sectionsHtml}
                </body>
            </html>`;

        try {
            printWindow.document.open();
            printWindow.document.write(printableHtml);
            printWindow.document.close();
        } catch {
            printWindow.document.body.innerHTML = '<p style="font-family:sans-serif;padding:16px;">Unable to render printable document. Please close this window and try again.</p>';
            toast.error("Unable to render printable document", { autoClose: 1600 });
            return;
        }

        const triggerPrint = () => {
            try {
                printWindow.focus();
                printWindow.print();
            } catch {
                toast.error("Unable to open print dialog", { autoClose: 1600 });
            }
        };

        // Prefer load-driven print; fallback timer avoids hanging if load event is skipped.
        printWindow.onload = () => {
            window.setTimeout(triggerPrint, 60);
        };
        window.setTimeout(triggerPrint, 220);
    }

    function handlePrint(mode) {
        if (mode === "bulk") {
            handlePrintBulkDocument();
            return;
        }

        if (mode === "single") {
            handlePrintSingleDocument();
            return;
        }

        if (printInProgressRef.current) return;
        printInProgressRef.current = true;
        const modeClass = mode === "bulk" ? "validation-print-bulk" : "validation-print-single";
        const root = document.documentElement;
        const mediaQueryList = typeof window.matchMedia === "function" ? window.matchMedia("print") : null;

        const clearPrintClasses = () => {
            document.body.classList.remove("validation-print-single", "validation-print-bulk");
            root.classList.remove("validation-print-single", "validation-print-bulk");
            if (printCleanupTimerRef.current) {
                window.clearTimeout(printCleanupTimerRef.current);
                printCleanupTimerRef.current = null;
            }
            printInProgressRef.current = false;
            window.removeEventListener("afterprint", clearPrintClasses);
            if (mediaQueryList) {
                if (typeof mediaQueryList.removeEventListener === "function") {
                    mediaQueryList.removeEventListener("change", onPrintMediaChange);
                } else if (typeof mediaQueryList.removeListener === "function") {
                    mediaQueryList.removeListener(onPrintMediaChange);
                }
            }
        };

        const onPrintMediaChange = (evt) => {
            if (!evt.matches) {
                clearPrintClasses();
            }
        };

        clearPrintClasses();
        document.body.classList.add(modeClass);
        root.classList.add(modeClass);
        window.addEventListener("afterprint", clearPrintClasses);
        if (mediaQueryList) {
            if (typeof mediaQueryList.addEventListener === "function") {
                mediaQueryList.addEventListener("change", onPrintMediaChange);
            } else if (typeof mediaQueryList.addListener === "function") {
                mediaQueryList.addListener(onPrintMediaChange);
            }
        }

        printCleanupTimerRef.current = window.setTimeout(() => {
            clearPrintClasses();
        }, 15000);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.print();
            });
        });
    }

    if (!userDetail) {
        return (
            <section className="page-scroll">
                <div className="page-scroll-inner validation-page-shell">
                    <h2>Validation Reports and Invoices</h2>
                    <p>Loading user permissions...</p>
                </div>
            </section>
        );
    }

    if (!isTenantPortal && roleId < 3) {
        return (
            <section className="page-scroll">
                <div className="page-scroll-inner validation-page-shell">
                    <h2>Validation Reports and Invoices</h2>
                    <p>Manager access required.</p>
                </div>
            </section>
        );
    }

    const rows = Array.isArray(report?.tenants) ? report.tenants : [];

    const totals = rows.reduce(
        (acc, row) => {
            acc.printed += Number(row.printedCount || 0);
            acc.redeemed += Number(row.redeemedCount || 0);
            acc.revenueCents += Number(row.redeemedAmountCents || 0);
            return acc;
        },
        { printed: 0, redeemed: 0, revenueCents: 0 },
    );

    const redeemedRate = totals.printed > 0 ? (totals.redeemed / totals.printed) * 100 : 0;
    const printableLineItems = itemizedTransactions.length ? itemizedTransactions : Array.isArray(invoice?.lineItems) ? invoice.lineItems : [];
    const shouldIncludeItemizedPrint = printableLineItems.length > 0;
    const invoiceStartDate = invoice?.startDate || startDate;
    const invoiceEndDate = invoice?.endDate || endDate;
    const invoicePeriodLabel = invoiceMode === "month" ? `Billing Month: ${invoice?.month || invoiceMonth}` : `Billing Range: ${invoiceStartDate || "--"} to ${invoiceEndDate || "--"}`;

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner validation-page-shell">
                <div className="validation-page-header screen-only-section">
                    <h2>Validation Reports and Invoices</h2>
                    <p>{isTenantPortal ? "Review your validation activity and monthly redeemed usage totals." : "Track printed vs redeemed validations by tenant and generate monthly invoice line items."}</p>
                </div>

                <div className="validation-section-intro validation-section-intro-quick screen-only-section">
                    <div className="validation-section-chip">Quick View</div>
                    <h3>Tenant Usage Snapshot</h3>
                    <p>Review printed, redeemed, and total tenant activity before generating invoices.</p>
                </div>

                <div className="validation-toolbar-card validation-toolbar-grid validation-quick-view-block screen-only-section">
                    {!isTenantPortal ? (
                        <FormGroup>
                            <Label for="validationReportLocation">Location</Label>
                            <Input
                                id="validationReportLocation"
                                type="select"
                                value={selectedLocationId}
                                onChange={(evt) => setSelectedLocationId(evt.target.value)}>
                                {accessibleLocations.length ? null : <option value="">No locations</option>}
                                {accessibleLocations.map((location) => (
                                    <option
                                        key={location.id}
                                        value={String(location.id)}>
                                        {location.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    ) : null}

                    <FormGroup>
                        <Label for="validationReportRange">Range</Label>
                        <Input
                            id="validationReportRange"
                            type="select"
                            value={reportRange}
                            onChange={(evt) => setReportRange(evt.target.value)}>
                            <option value="today">Current Day</option>
                            <option value="week">Past Week (incl. today)</option>
                            <option value="month">Past Month (from first day)</option>
                            <option value="last60">Last 60 Days</option>
                            <option value="last90">Last 90 Days</option>
                            <option value="custom">Custom</option>
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label for="validationReportStartDate">Start Date</Label>
                        <Input
                            id="validationReportStartDate"
                            type="date"
                            value={startDate}
                            onChange={(evt) => setStartDate(evt.target.value)}
                            disabled={reportRange !== "custom"}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="validationReportEndDate">End Date</Label>
                        <Input
                            id="validationReportEndDate"
                            type="date"
                            value={endDate}
                            onChange={(evt) => setEndDate(evt.target.value)}
                            disabled={reportRange !== "custom"}
                        />
                    </FormGroup>
                </div>

                <div className="validation-kpi-grid validation-quick-view-block screen-only-section">
                    <div className="validation-kpi-card">
                        <span className="validation-kpi-label">Printed</span>
                        <strong>{totals.printed}</strong>
                    </div>
                    <div className="validation-kpi-card">
                        <span className="validation-kpi-label">Redeemed</span>
                        <strong>{totals.redeemed}</strong>
                    </div>
                    <div className="validation-kpi-card">
                        <span className="validation-kpi-label">Redemption Rate</span>
                        <strong>{formatPercent(redeemedRate)}</strong>
                    </div>
                    <div className="validation-kpi-card">
                        <span className="validation-kpi-label">Tenant Revenue</span>
                        <strong>{formatCents(totals.revenueCents)}</strong>
                    </div>
                </div>

                <div className="validation-panel validation-quick-view-block screen-only-section">
                    <h3>By Tenant</h3>
                    {isLoadingReport ? <p>Loading report...</p> : null}
                    {!isLoadingReport && !rows.length ? <p>No validation activity in this date range.</p> : null}
                    {!isLoadingReport && rows.length ? (
                        <div className="validation-list-wrap">
                            <table className="validation-table">
                                <thead>
                                    <tr>
                                        <th>Tenant</th>
                                        <th>Printed</th>
                                        <th>Redeemed</th>
                                        <th>Rate</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const rate = Number(row.printedCount || 0) > 0 ? (Number(row.redeemedCount || 0) / Number(row.printedCount || 0)) * 100 : 0;
                                        return (
                                            <tr key={row.tenantId}>
                                                <td>{row.tenantName}</td>
                                                <td>{row.printedCount}</td>
                                                <td>{row.redeemedCount}</td>
                                                <td>{formatPercent(rate)}</td>
                                                <td>{formatCents(row.redeemedAmountCents)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>

                <div className="validation-section-intro validation-section-intro-invoice no-print">
                    <div className="validation-section-chip">Invoice Actions</div>
                    <h3>Invoice Generation And Printing</h3>
                    <p>Configure billing mode, preview details, and print or email invoices.</p>
                </div>

                <div className="validation-panel validation-invoice-block single-print-section">
                    <div className="validation-panel-head-row">
                        <h3>Monthly Tenant Invoice</h3>
                        {invoice ? (
                            <Button
                                color="secondary"
                                className="no-print"
                                onClick={() => handlePrint("single")}>
                                Print Invoice
                            </Button>
                        ) : null}
                    </div>

                    <div className="validation-toolbar-grid no-print">
                        {isTenantPortal ? (
                            <FormGroup>
                                <Label for="validationInvoiceTenant">Tenant</Label>
                                <Input
                                    id="validationInvoiceTenant"
                                    type="text"
                                    value={tenants[0]?.name || ""}
                                    readOnly
                                />
                            </FormGroup>
                        ) : (
                            <FormGroup>
                                <Label for="validationInvoiceTenant">Tenant</Label>
                                <Input
                                    id="validationInvoiceTenant"
                                    type="select"
                                    value={selectedTenantId}
                                    onChange={(evt) => setSelectedTenantId(evt.target.value)}>
                                    {tenants.length ? null : <option value="">No tenants</option>}
                                    {tenants.map((tenant) => (
                                        <option
                                            key={tenant.id}
                                            value={String(tenant.id)}>
                                            {tenant.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        )}

                        <FormGroup>
                            <Label for="validationInvoiceMonth">Month</Label>
                            <Input
                                id="validationInvoiceMonth"
                                type="month"
                                value={invoiceMonth}
                                disabled={invoiceMode !== "month"}
                                onChange={(evt) => {
                                    const nextValue = evt.target.value;
                                    setInvoiceMonth(nextValue);
                                    if (reportRange === "month") {
                                        const computed = computeRangeFromPreset("month", {
                                            todayIso,
                                            selectedMonth: nextValue,
                                        });
                                        if (computed) {
                                            setStartDate(computed.startDate);
                                            setEndDate(computed.endDate);
                                        }
                                    }
                                }}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="validationInvoiceMode">Invoice Mode</Label>
                            <Input
                                id="validationInvoiceMode"
                                type="select"
                                value={invoiceMode}
                                onChange={(evt) => {
                                    const nextMode = evt.target.value;
                                    setInvoiceMode(nextMode);
                                    if (nextMode === "range") {
                                        setReportRange("custom");
                                    }
                                }}>
                                <option value="month">Monthly</option>
                                <option value="range">Date Range</option>
                            </Input>
                        </FormGroup>

                        {invoiceMode === "range" ? (
                            <>
                                <FormGroup>
                                    <Label for="validationInvoiceStartDate">Invoice Start Date</Label>
                                    <Input
                                        id="validationInvoiceStartDate"
                                        type="date"
                                        value={startDate}
                                        onChange={(evt) => setStartDate(evt.target.value)}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label for="validationInvoiceEndDate">Invoice End Date</Label>
                                    <Input
                                        id="validationInvoiceEndDate"
                                        type="date"
                                        value={endDate}
                                        onChange={(evt) => setEndDate(evt.target.value)}
                                    />
                                </FormGroup>
                            </>
                        ) : null}

                        <FormGroup className="validation-form-action-row">
                            <Label>&nbsp;</Label>
                            <Button
                                color="primary"
                                type="button"
                                onClick={handleLoadInvoice}
                                disabled={isLoadingInvoice || !selectedTenantId || (invoiceMode === "month" ? !invoiceMonth : !startDate || !endDate)}>
                                {isLoadingInvoice ? "Loading..." : "Load Invoice"}
                            </Button>
                        </FormGroup>

                        <FormGroup className="validation-form-action-row">
                            <Label>&nbsp;</Label>
                            <Button
                                color="info"
                                type="button"
                                onClick={handleViewItemizedTransactions}
                                disabled={isLoadingItemized || !selectedTenantId || !startDate || !endDate}>
                                {isLoadingItemized ? "Loading..." : "View Itemized Transactions"}
                            </Button>
                        </FormGroup>
                    </div>

                    {!isTenantPortal ? (
                        <div className="validation-toolbar-grid no-print">
                            <FormGroup>
                                <Label for="validationInvoiceEmailTo">Invoice Email To</Label>
                                <Input
                                    id="validationInvoiceEmailTo"
                                    type="email"
                                    value={invoiceEmailTo}
                                    placeholder="tenant@client.com"
                                    onChange={(evt) => setInvoiceEmailTo(evt.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="validationInvoiceEmailMessage">Optional Message</Label>
                                <Input
                                    id="validationInvoiceEmailMessage"
                                    value={invoiceEmailMessage}
                                    onChange={(evt) => setInvoiceEmailMessage(evt.target.value)}
                                    placeholder="Thanks, here is your monthly validation invoice"
                                />
                            </FormGroup>

                            <FormGroup className="validation-form-action-row">
                                <Label>&nbsp;</Label>
                                <Button
                                    color="success"
                                    type="button"
                                    onClick={handleEmailInvoice}
                                    disabled={isSendingInvoiceEmail || !selectedTenantId || !invoiceMonth || invoiceMode !== "month"}>
                                    {isSendingInvoiceEmail ? "Sending..." : "One-Click Email Invoice"}
                                </Button>
                            </FormGroup>
                        </div>
                    ) : null}

                    {itemizedTransactions.length ? (
                        <div className="validation-invoice-wrap no-print">
                            <h4>Itemized Transactions Preview ({itemizedTransactions.length})</h4>
                            <div className="validation-list-wrap">
                                <table className="validation-table">
                                    <thead>
                                        <tr>
                                            <th>Redeemed At</th>
                                            <th>Token</th>
                                            <th>Ticket</th>
                                            <th>Mode</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itemizedTransactions.map((row, idx) => (
                                            <tr key={`itemized-${row.validationTokenId || "token"}-${row.redeemedAt || "na"}-${idx}`}>
                                                <td>{row.redeemedAt ? new Date(row.redeemedAt).toLocaleString() : "--"}</td>
                                                <td>{row.tokenCode}</td>
                                                <td>{row.ticketNum || "--"}</td>
                                                <td>{row.coverageMode}</td>
                                                <td>{formatCents(row.redeemedAmountCents)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    {invoice ? (
                        <div className="validation-invoice-wrap">
                            <div className={`validation-invoice-cover ${shouldIncludeItemizedPrint ? "has-print-appendix" : ""}`}>
                                <div className="validation-cover-header-row">
                                    <div>
                                        <h4>Tenant Validation Invoice</h4>
                                        <p>{invoicePeriodLabel}</p>
                                    </div>
                                    <div className="validation-cover-status-pill">Issued</div>
                                </div>

                                <div className="validation-cover-grid">
                                    <div className="validation-cover-item">
                                        <span>Tenant</span>
                                        <strong>{invoice?.tenant?.name || "--"}</strong>
                                    </div>
                                    <div className="validation-cover-item">
                                        <span>Location</span>
                                        <strong>{invoice?.tenant?.locationId ? `Location ${invoice.tenant.locationId}` : selectedLocationId ? `Location ${selectedLocationId}` : "--"}</strong>
                                    </div>
                                    <div className="validation-cover-item">
                                        <span>Total Line Items</span>
                                        <strong>{invoice?.totals?.lineItemsCount || printableLineItems.length || 0}</strong>
                                    </div>
                                    <div className="validation-cover-item">
                                        <span>Total Due</span>
                                        <strong>{formatCents(invoice?.totals?.totalAmountCents || 0)}</strong>
                                    </div>
                                </div>

                                <div className="validation-result-banner">
                                    <strong>{invoice?.tenant?.name || "Tenant"}</strong> • {invoiceMode === "month" ? invoice?.month || invoiceMonth : `${invoiceStartDate || "--"} to ${invoiceEndDate || "--"}`} • {invoice?.totals?.lineItemsCount || printableLineItems.length || 0} line items • {formatCents(invoice?.totals?.totalAmountCents || 0)}
                                </div>
                            </div>

                            {shouldIncludeItemizedPrint ? (
                                <div className="validation-itemized-print-page print-only">
                                    <h4>Itemized Transactions ({printableLineItems.length})</h4>
                                    <div className="validation-list-wrap">
                                        <table className="validation-table">
                                            <thead>
                                                <tr>
                                                    <th colSpan="5">
                                                        {invoice?.tenant?.name || "Tenant"} • {invoicePeriodLabel}
                                                    </th>
                                                </tr>
                                                <tr>
                                                    <th>Redeemed At</th>
                                                    <th>Token</th>
                                                    <th>Ticket</th>
                                                    <th>Mode</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {printableLineItems.map((row, idx) => (
                                                    <tr key={`print-itemized-${row.validationTokenId || "token"}-${row.redeemedAt || "na"}-${idx}`}>
                                                        <td>{row.redeemedAt ? new Date(row.redeemedAt).toLocaleString() : "--"}</td>
                                                        <td>{row.tokenCode}</td>
                                                        <td>{row.ticketNum || "--"}</td>
                                                        <td>{row.coverageMode}</td>
                                                        <td>{formatCents(row.redeemedAmountCents)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                {!isTenantPortal ? (
                    <div className="validation-panel validation-invoice-block bulk-print-section">
                        <div className="validation-panel-head-row no-print">
                            <h3>Bulk Tenant Invoices</h3>
                            {bulkInvoices.length ? (
                                <Button
                                    color="secondary"
                                    onClick={() => handlePrint("bulk")}>
                                    Print All Invoices
                                </Button>
                            ) : null}
                        </div>

                        <div className="validation-toolbar-grid no-print">
                            <FormGroup>
                                <Label for="validationBulkScopeType">Scope</Label>
                                <Input
                                    id="validationBulkScopeType"
                                    type="select"
                                    value={bulkScopeType}
                                    onChange={(evt) => setBulkScopeType(evt.target.value)}>
                                    <option value="location">Location</option>
                                    <option value="podium">Podium</option>
                                </Input>
                            </FormGroup>

                            <FormGroup>
                                <Label for="validationBulkScopeId">{bulkScopeType === "location" ? "Location" : "Podium"}</Label>
                                <Input
                                    id="validationBulkScopeId"
                                    type="select"
                                    value={bulkScopeId}
                                    onChange={(evt) => setBulkScopeId(evt.target.value)}>
                                    {(bulkScopeType === "location" ? accessibleLocations : accessiblePodiums).length ? null : <option value="">No options</option>}
                                    {(bulkScopeType === "location" ? accessibleLocations : accessiblePodiums).map((row) => (
                                        <option
                                            key={row.id}
                                            value={String(row.id)}>
                                            {row.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>

                            <FormGroup className="validation-form-action-row">
                                <Label>&nbsp;</Label>
                                <Button
                                    color="primary"
                                    type="button"
                                    onClick={handleLoadBulkInvoices}
                                    disabled={isLoadingBulkInvoices || !bulkScopeId || (invoiceMode === "month" ? !invoiceMonth : !startDate || !endDate)}>
                                    {isLoadingBulkInvoices ? "Loading..." : "Load All Invoices"}
                                </Button>
                            </FormGroup>
                        </div>

                        {bulkScopeMeta ? (
                            <div className="validation-result-banner">
                                Scope: <strong>{String(bulkScopeMeta.scopeType || bulkScopeType)}</strong> #{String(bulkScopeMeta.scopeId || bulkScopeId)} • Invoices: <strong>{bulkInvoices.length}</strong>
                            </div>
                        ) : null}

                        {bulkInvoices.length ? (
                            <div className="validation-bulk-invoices-stack">
                                {bulkInvoices.map((bulkInvoice, idx) => {
                                    const bulkLines = Array.isArray(bulkInvoice?.lineItems) ? bulkInvoice.lineItems : [];
                                    const bulkPeriodLabel = invoiceMode === "month" ? `Billing Month: ${bulkInvoice?.month || invoiceMonth}` : `Billing Range: ${bulkInvoice?.range?.startDate || startDate || "--"} to ${bulkInvoice?.range?.endDate || endDate || "--"}`;

                                    return (
                                        <div
                                            key={`bulk-invoice-${bulkInvoice?.tenant?.id || idx}`}
                                            className="validation-bulk-invoice-item">
                                            <div className={`validation-invoice-cover${bulkLines.length ? " has-print-appendix" : ""}`}>
                                                <div className="validation-cover-header-row">
                                                    <div>
                                                        <h4>Tenant Validation Invoice</h4>
                                                        <p>{bulkPeriodLabel}</p>
                                                    </div>
                                                    <div className="validation-cover-status-pill">Issued</div>
                                                </div>

                                                <div className="validation-cover-grid">
                                                    <div className="validation-cover-item">
                                                        <span>Tenant</span>
                                                        <strong>{bulkInvoice?.tenant?.name || "--"}</strong>
                                                    </div>
                                                    <div className="validation-cover-item">
                                                        <span>Location</span>
                                                        <strong>{bulkInvoice?.tenant?.locationId ? `Location ${bulkInvoice.tenant.locationId}` : "--"}</strong>
                                                    </div>
                                                    <div className="validation-cover-item">
                                                        <span>Total Line Items</span>
                                                        <strong>{bulkInvoice?.totals?.lineItemsCount || bulkLines.length || 0}</strong>
                                                    </div>
                                                    <div className="validation-cover-item">
                                                        <span>Total Due</span>
                                                        <strong>{formatCents(bulkInvoice?.totals?.totalAmountCents || 0)}</strong>
                                                    </div>
                                                </div>

                                                <div className="validation-result-banner">
                                                    <strong>{bulkInvoice?.tenant?.name || "Tenant"}</strong> • {invoiceMode === "month" ? bulkInvoice?.month || invoiceMonth : `${bulkInvoice?.range?.startDate || startDate || "--"} to ${bulkInvoice?.range?.endDate || endDate || "--"}`} • {bulkInvoice?.totals?.lineItemsCount || bulkLines.length || 0} line items • {formatCents(bulkInvoice?.totals?.totalAmountCents || 0)}
                                                </div>
                                            </div>

                                            {bulkLines.length ? (
                                                <div className="validation-itemized-print-page print-only">
                                                    <h4>Itemized Transactions ({bulkLines.length})</h4>
                                                    <div className="validation-list-wrap">
                                                        <table className="validation-table">
                                                            <thead>
                                                                <tr>
                                                                    <th colSpan="5">
                                                                        {bulkInvoice?.tenant?.name || "Tenant"} • {bulkPeriodLabel}
                                                                    </th>
                                                                </tr>
                                                                <tr>
                                                                    <th>Redeemed At</th>
                                                                    <th>Token</th>
                                                                    <th>Ticket</th>
                                                                    <th>Mode</th>
                                                                    <th>Amount</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {bulkLines.map((row, lineIdx) => (
                                                                    <tr key={`bulk-line-${bulkInvoice?.tenant?.id || idx}-${row.validationTokenId || "token"}-${lineIdx}`}>
                                                                        <td>{row.redeemedAt ? new Date(row.redeemedAt).toLocaleString() : "--"}</td>
                                                                        <td>{row.tokenCode}</td>
                                                                        <td>{row.ticketNum || "--"}</td>
                                                                        <td>{row.coverageMode}</td>
                                                                        <td>{formatCents(row.redeemedAmountCents)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="no-print">Load bulk invoices to review and print every tenant invoice in one batch.</p>
                        )}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default ValidationReportsPage;
